<script lang="ts">
	import '@awesome.me/kit-f1d1181ead/icons/css/all.min.css';
	import { cart, type cartData } from '$stores/cart';
	import { isCartOpen } from '$stores/cartComponentManager';
	import { goto, onNavigate } from '$app/navigation';
	import DatePicker from './DatePicker.svelte';
	import { flavours } from '$utils/Flavours';
	import { onMount } from 'svelte';
	import {
		deliveryCharge,
		deliveryMinimum,
		formatFulfilmentDates
	} from '$utils/common';

	let cartDialog: HTMLDialogElement;
	let chosenDate: Date | undefined = $cart.chosenDate || undefined;
	let invalidDate: boolean = false;
	let deliverySelectValue: string = 'delivery';
	let previousDeliveryStatus: boolean = $cart.delivery;
	let promotionApplied: boolean = false;
	let promotionButtonClicked: boolean = false;
	let discountCode: string = '';
	let message: string = '';

	$: if ($cart.promotion) {
		promotionApplied = true;
	}

	$: if ($isCartOpen && cartDialog) {
		cartDialog.showModal();
		//Checks if the users chosenDate is within 2 days
		if (
			chosenDate !== undefined &&
			chosenDate < new Date(new Date().setDate(new Date().getDate() + 2))
		) {
			chosenDate = undefined;
		}
	} else if (!$isCartOpen && cartDialog) {
		cartDialog.close();
	}

	onMount(() => {
		if ($cart.delivery === true) {
			deliverySelectValue = 'delivery';
		} else {
			deliverySelectValue = 'collection';
		}
	});

	$: if ($cart.delivery !== previousDeliveryStatus) {
		chosenDate = undefined;
		cart.changeChosenDate(chosenDate);
		previousDeliveryStatus = $cart.delivery;
	}

	$: if (chosenDate !== undefined && chosenDate != $cart.chosenDate) {
		invalidDate = false;
		cart.changeChosenDate(chosenDate);
	}

	onNavigate(() => {
		if ($isCartOpen && cartDialog) {
			isCartOpen.set(false);
		}
	});

	function checkCart() {
		if ($cart.items.length == 0) {
			isCartOpen.set(false);
		}
	}

	function changeCartQuantity(index: number, event: Event) {
		const target = event.target as HTMLInputElement;
		const value = parseInt(target.value);
		if (value > 0) {
			cart.setQuantity(index, value);
		} else {
			cart.removeItem(index);
			checkCartPromotion();
		}
	}

	async function checkCartPromotion() {
		if ($cart.promotion) {
			let response: Response = await fetch('/api/checkPromotion', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ code: $cart.promotion.code, cart: $cart })
			});
			response = await response.json();
			if ((response as any).isValid == false) {
				cart.removePromotion();
			}
		}
	}

	function sortExtras(
		a: { name: string; value: string | undefined; price: number },
		b: { name: string; value: string | undefined; price: number }
	) {
		if (a.name < b.name) {
			return -1;
		}
		if (a.name > b.name) {
			return 1;
		}
		return 0;
	}

	function closeDialogWhenClickedOutside(event: MouseEvent) {
		if (event.target === cartDialog) {
			isCartOpen.set(false);
		}
	}

	function updateDeliveryOption(event: Event) {
		const target = event.target as HTMLSelectElement;
		if (target.value == 'delivery') {
			cart.changeDelivery(true);
			chosenDate = undefined;
		} else {
			cart.changeDelivery(false);
			chosenDate = undefined;
		}
	}

	async function applyPromotion() {
		promotionApplied = await cart.addPromotion(
			discountCode.trim().toLowerCase(),
			JSON.parse(JSON.stringify($cart))
		);
		promotionButtonClicked = true;
		setTimeout(() => {
			promotionButtonClicked = false;
		}, 3000);
	}

	async function confirmCart(): Promise<boolean> {
		if ($cart.delivery && $cart.total < deliveryMinimum + deliveryCharge) {
			return false;
		} else if (
			chosenDate !== undefined &&
			chosenDate < new Date(new Date().setDate(new Date().getDate() + 2))
		) {
			invalidDate = true;
			return false;
		}
		try {
			const response = await fetch('/api/checkAvailability', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					itemIds: $cart.items.map((item) => item.product._id)
				})
			});
			const data = await response.json();
			if (data && data.length > 0) {
				if (data.length > 1) {
					const unavailableItems =
						data
							.map((item: { _id: string; name: string }) => item.name)
							.slice(0, -1)
							.join(', ') +
						' and ' +
						data[data.length - 1].name;
					message = `Sorry but ${unavailableItems} are currently not available. Please remove them from your cart.`;
				} else {
					message = `Sorry but ${data[0].name} is currently not available. Please remove it from your cart.`;
				}
				return false;
			}
		} catch (e) {
			console.error(e);
		}
		return true;
	}

	$: {
		$cart.items.length;
		let fofilmentDateFound: boolean = false;
		for (let newProduct of $cart.items) {
			if (newProduct.product.fulfilmentDates) {
				fofilmentDateFound = true;
				if (!$cart.fulfilmentDates) {
					$cart.fulfilmentDates = {
						start: newProduct.product.fulfilmentDates.start,
						end: newProduct.product.fulfilmentDates.end
					};
				} else {
					if (
						newProduct.product.fulfilmentDates.start >
						$cart.fulfilmentDates.start
					) {
						$cart.fulfilmentDates.start =
							newProduct.product.fulfilmentDates.start;
					}
					if (
						newProduct.product.fulfilmentDates.end < $cart.fulfilmentDates.end
					) {
						$cart.fulfilmentDates.end = newProduct.product.fulfilmentDates.end;
					}
				}
				if ($cart.chosenDate) {
					let tempStartDate: Date | undefined = undefined;
					let tempEndDate: Date | undefined = undefined;
					if ($cart.fulfilmentDates.start.split('-').length > 2) {
						tempStartDate = new Date($cart.fulfilmentDates.start);
					} else {
						tempStartDate = new Date(
							new Date().getFullYear(),
							parseInt($cart.fulfilmentDates.start.split('-')[0]) - 1,
							parseInt($cart.fulfilmentDates.start.split('-')[1])
						);
					}
					if ($cart.fulfilmentDates.end.split('-').length > 2) {
						tempEndDate = new Date($cart.fulfilmentDates.end);
					} else {
						tempEndDate = new Date(
							new Date().getFullYear(),
							parseInt($cart.fulfilmentDates.end.split('-')[0]) - 1,
							parseInt($cart.fulfilmentDates.end.split('-')[1])
						);
					}
					if (
						tempStartDate > new Date($cart.chosenDate) ||
						tempEndDate < new Date($cart.chosenDate)
					) {
						console.log('Chosen date is not within fulfilment dates');
						chosenDate = undefined;
						cart.changeChosenDate(chosenDate);
					}
				}
			}
		}
		if (!fofilmentDateFound) {
			$cart.fulfilmentDates = undefined;
		}
	}
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<dialog
	bind:this={cartDialog}
	on:close={() => {
		if ($isCartOpen) {
			isCartOpen.set(false);
		}
	}}
	on:click={(event) => closeDialogWhenClickedOutside(event)}
	class="mx-auto w-full px-4 py-4 sm:px-6 sm:py-6 md:w-1/2 lg:px-8 lg:py-8"
>
	<header class="flex flex-col text-center">
		<!-- svelte-ignore a11y-autofocus -->
		<button
			class="ml-auto w-fit"
			autofocus
			aria-label="Close cart"
			on:click={() => {
				isCartOpen.set(false);
			}}
			><span class="fa-duotone fa-circle-xmark text-logoBackgroundBlue"
			></span></button
		>
		<h2 class="text-xl font-bold text-gray-900 sm:text-3xl">Your Cart</h2>
	</header>
	<div class="my-8 h-px bg-gray-100"></div>
	{#if $cart.items.length > 0}
		<ul class="space-y-4">
			{#each $cart.items as item, index}
				<li class="flex gap-4">
					<div class="w-16">
						<img
							data-src={`https://takethecake.gumlet.io/${item.product._id}/${item.product.thumbnail.src.replace(' ', '%20')}`}
							alt={item.product.thumbnail.alt}
							class="rounded"
						/>
					</div>
					<div class="flex flex-1 flex-col">
						<h3 class="text-md font-semibold text-gray-900">
							{item.product.name}
						</h3>
						<div class="mt-0.5 space-y-px text-xs text-gray-600">
							{#if item.product.fulfilmentDates}
								<p class="text-red-500">
									Availability: {formatFulfilmentDates(
										item.product.fulfilmentDates.start
									)} - {formatFulfilmentDates(item.product.fulfilmentDates.end)}
								</p>{/if}
							{#if item.product.size?.name}
								{#if typeof item.product.size.name == 'string' && item.product.size.name.includes('.')}
									<p>
										Size: {item.product.size.name.split('.')[0]}in top, {item.product.size.name.split(
											'.'
										)[1]}in bottom
									</p>
								{:else}
									<p>Size: {item.product.size.name}in</p>
								{/if}
							{:else if item.product.quantity}
								<p>Quantity: {item.product.quantity.name}</p>
							{/if}
							{#if item.product.flavour}
								<p>
									Flavour: {flavours[item.product.flavour].name}
									{flavours[item.product.flavour].price
										? ' €' + flavours[item.product.flavour].price
										: ''}
								</p>
							{/if}
							{#if item.product.topFlavour && item.product.bottomFlavour}
								<p>Top Flavour: {flavours[item.product.topFlavour].name}</p>
								<p>
									Bottom Flavour: {flavours[item.product.bottomFlavour].name}
								</p>
							{/if}
							{#if item.product.shape}
								<p>Shape: {item.product.shape}</p>
							{/if}
							{#if item.product.packOneFlavour}
								<p>
									Pack One Flavour: {flavours[item.product.packOneFlavour].name}
								</p>
							{/if}
							{#if item.product.packTwoFlavour}
								<p>Pack Two Flavour: {flavours[item.product.packTwoFlavour]}</p>
							{/if}
							{#if item.product.packThreeFlavour}
								<p>
									Pack Three Flavour: {flavours[item.product.packThreeFlavour]}
								</p>
							{/if}
							{#if item.product.packFourFlavour}
								<p>
									Pack Four Flavour: {flavours[item.product.packFourFlavour]}
								</p>
							{/if}
							{#if item.product.extras}
								{#each item.product.extras.sort(sortExtras) as extra}
									<p>
										{extra.name}:
										{#if extra.value}
											({extra.value})
										{/if}
										€{extra.price}
									</p>
								{/each}
							{/if}
							<p class="font-semibold text-gray-700">
								Price: €{item.product.price * item.quantity}
							</p>
						</div>
					</div>

					<div
						class="flex flex-1 flex-col-reverse items-center justify-end gap-2"
					>
						<button
							on:click={() => {
								cart.decreaseQuantity(index);
								checkCartPromotion();
							}}
							aria-label="Decrease quantity"
							class="hover:text-gray-600 focus:text-gray-600">-</button
						>
						<input
							class="h-8 w-12 rounded border-gray-200 bg-gray-50 p-0 text-center text-xs text-gray-600 [-moz-appearance:_textfield] hover:border-logoBackgroundBlue hover:outline-none hover:ring-1 hover:ring-logoBackgroundBlue focus:border-logoBackgroundBlue focus:outline-none focus:ring-1 focus:ring-logoBackgroundBlue [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
							type="number"
							value={item.quantity}
							on:change={(event) => changeCartQuantity(index, event)}
						/>
						<button
							on:click={() => {
								cart.increaseQuantity(index);
							}}
							aria-label="Increase quantity"
							class="hover:text-gray-600 focus:text-gray-600">+</button
						>
					</div>
					<div>
						<button
							class="h-4 w-4"
							on:click={() => {
								cart.removeItem(index);
								checkCartPromotion();
								checkCart();
							}}
							aria-label={`Remove ${item.product.name} from cart`}
							><span class="fa-duotone fa-trash-can text-logoBackgroundBlue"
							></span></button
						>
					</div>
				</li>
			{/each}
		</ul>
		<div class="mt-8 flex flex-col space-y-8 border-t border-gray-100 pt-8">
			<div class="space-y-0.5">
				<h2 class="text-md text-center font-semibold text-gray-900">
					Delivery / Collection
				</h2>
				<div class="flex flex-col text-sm text-gray-600">
					<p>
						* Orders over €{deliveryMinimum} are available for delivery but only
						inside Dublin
					</p>
					<p>
						* Deliveries outside Dublin can sometimes be facilitated but you
						need to co ordinate this with Krissy via email <a
							class="text-logoBackgroundBlue hover:text-logobackgroundBlueRgba focus:text-logobackgroundBlueRgba"
							href="mailto:krissy@takethecake.ie">krissy@takethecake.ie</a
						>
						ahead of ordering.
					</p>
					<p>
						* Orders under €{deliveryMinimum} excluding the delivery charge are only
						available for collection
					</p>
				</div>
				<div class="pt-2">
					{#if $cart.total < deliveryMinimum + deliveryCharge && $cart.delivery}
						<p class="text-center text-red-500">
							You need to add at least €{deliveryMinimum +
								deliveryCharge -
								$cart.total} to be eligible for delivery.
						</p>
					{/if}
					<select
						class="w-full rounded-lg border-gray-300 text-gray-700 sm:text-sm"
						on:input={(event) => updateDeliveryOption(event)}
						bind:value={deliverySelectValue}
					>
						<option value="delivery"
							>Dublin only delivery: €{deliveryCharge}</option
						>
						<option value="collection">Collection: free</option>
					</select>
					<div class="mt-4 flex justify-center">
						<DatePicker bind:chosenDate />
					</div>
					{#if invalidDate}
						<p class="text-center text-red-500" role="alert">
							Please choose a date at least 2 days from today.
						</p>
						<p>**If you are using Safari then please try another browser**</p>
					{:else if chosenDate !== undefined}
						<p class="text-center text-sm text-gray-600">
							{$cart.delivery == true ? 'Delivery' : 'Collection'} date: {new Date(
								chosenDate
							).toDateString()}
						</p>
					{/if}
				</div>
			</div>

			<div class="mt-8 flex flex-col space-y-8 border-t border-gray-100 pt-8">
				<div class="space-y-0.5">
					<h2 class="text-md text-center font-semibold text-gray-900">
						Promotions
					</h2>
					<div class="flex gap-2 text-gray-600">
						<span class="fa-regular fa-tag my-auto"></span>
						<input
							bind:value={discountCode}
							aria-label="Promotion code"
							type="text"
							class="w-full rounded-lg border-gray-300 text-gray-700 sm:text-sm"
							placeholder="Enter your code here"
						/>
						<button
							class="rounded bg-logoBackgroundBlue px-5 py-3 text-sm text-gray-100 transition hover:bg-logobackgroundBlueRgba focus:bg-logobackgroundBlueRgba"
							on:click={applyPromotion}
							disabled={!discountCode.trim()}
						>
							Apply
						</button>
					</div>
					{#if promotionApplied && $cart.promotion}
						<p role="alert" class="text-center text-green-500">
							Promotion applied!
						</p>
						<p class="text-center">
							{$cart.promotion.code}
							<button
								aria-label="Remove Promotion"
								on:click={cart.removePromotion}
								><span class="fa-duotone fa-xmark"></span></button
							>
						</p>
					{:else if discountCode && !promotionApplied && promotionButtonClicked}
						<p role="alert" class="text-center text-red-500">
							Invalid promotion code
						</p>
					{/if}
				</div>
			</div>
			<div class="h-px bg-gray-100"></div>
			<div>
				<h2 class="text-md text-center font-semibold text-gray-900">
					Overview
				</h2>
				<div class="text-sm text-gray-600">
					<div class="flex justify-between">
						<span>Items:</span> <span>{$cart.items.length}</span>
					</div>
					<div class="flex justify-between">
						<span>Subtotal:</span>
						<span
							>€{$cart.items.reduce(
								(total, item) => total + item.product.price * item.quantity,
								0
							)}</span
						>
					</div>
					<div class="flex justify-between">
						<span>{$cart.delivery ? 'Delivery:' : 'Collection:'}</span><span
							>{$cart.delivery ? '€' + deliveryCharge : 'Free'}</span
						>
					</div>
					{#if $cart.promotion}
						<div class="flex justify-between">
							<span>Discount:</span><span>
								{#if $cart.promotion.discountAmountType === 'euro' && $cart.promotion.discountAmount}
									-€{$cart.promotion.discountAmount}
								{:else if $cart.promotion.discountAmountType === 'percent' && $cart.promotion.discountAmount}
									-€{($cart.total / (100 - $cart.promotion.discountAmount)) *
										$cart.promotion.discountAmount}
								{/if}
							</span>
						</div>
					{/if}
					<div class="flex justify-between font-semibold text-gray-700">
						<span>Total:</span> <span>€{$cart.total}</span>
					</div>
				</div>
			</div>

			{#if message}
				<p
					role="alert"
					aria-live="polite"
					aria-atomic="true"
					class="text-center text-red-500"
				>
					{message}
				</p>
			{/if}

			<div class="my-8 h-px bg-gray-100"></div>
			<div class="flex flex-col space-y-4 text-center">
				<button
					class="block rounded border border-logoBackgroundBlue px-5 py-3 text-sm text-logoBackgroundBlue transition hover:ring-1 hover:ring-logobackgroundBlueRgba"
					on:click={() => {
						chosenDate = undefined;
						deliverySelectValue = 'delivery';
						cart.clear();
						isCartOpen.set(false);
					}}>Empty cart</button
				>
				<button
					disabled={$cart.chosenDate == undefined ||
						($cart.delivery && $cart.total < deliveryMinimum + deliveryCharge)}
					class="block rounded bg-logoBackgroundBlue px-5 py-3 text-sm text-gray-100 transition hover:bg-logobackgroundBlueRgba focus:bg-logobackgroundBlueRgba disabled:cursor-not-allowed disabled:bg-gray-300"
					on:click={async () => {
						if (await confirmCart()) {
							goto('/checkout');
						}
					}}>Checkout</button
				>
				<a
					href="/shop"
					class="inline-block text-sm text-logoBackgroundBlue underline underline-offset-4 transition hover:text-logobackgroundBlueRgba"
				>
					Continue shopping
				</a>
			</div>
		</div>
	{:else}
		<div class="flex flex-col justify-center space-y-4 text-center">
			<p class="text-sm text-gray-600">There is nothing in your cart!</p>
			<a
				href="/shop"
				class="inline-block text-sm text-logoBackgroundBlue underline underline-offset-4 transition hover:text-logobackgroundBlueRgba"
			>
				Continue shopping
			</a>
		</div>
	{/if}
</dialog>
